import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import client from '../api/client';

const navItems = [
  { path: '/overview',        label: 'Overview',         icon: 'bi-speedometer2',      roles: ['reviewer', 'approver', 'admin'] },
  { path: '/invoices',        label: 'Invoices',          icon: 'bi-receipt',           roles: ['reviewer', 'approver', 'admin'] },
  { path: '/credit-notes',    label: 'Credit Notes',      icon: 'bi-credit-card',       roles: ['reviewer', 'approver', 'admin'] },
  { path: '/exceptions',      label: 'Exceptions',        icon: 'bi-exclamation-triangle', roles: ['reviewer', 'approver', 'admin'] },
  { path: '/suppliers',       label: 'Suppliers',         icon: 'bi-building',          roles: ['reviewer', 'approver', 'admin'] },
  { path: '/reconciliation',  label: 'Reconciliation',    icon: 'bi-arrow-left-right',  roles: ['reviewer', 'approver', 'admin'] },
  { path: '/statements',      label: 'Statements',        icon: 'bi-file-text',         roles: ['reviewer', 'approver', 'admin'] },
  { path: '/processing-runs', label: 'Processing Runs',   icon: 'bi-gear',              roles: ['approver', 'admin'] },
  { path: '/settings',        label: 'Settings',          icon: 'bi-sliders',           roles: ['approver', 'admin'] },
];

const appAdminItems = [
  { path: '/overview',                 label: 'Dashboard',      icon: 'bi-speedometer2' },
  { path: '/profile',                  label: 'Profile',        icon: 'bi-person-circle' },
  { path: '/app-admin',                label: 'Organizations',  icon: 'bi-diagram-3' },
  { path: '/app-admin/users',          label: 'Users',          icon: 'bi-people' },
  { path: '/app-admin/configuration',  label: 'Configuration',  icon: 'bi-toggles' },
  { path: '/app-admin/api',            label: 'API',            icon: 'bi-activity' },
  { path: '/app-admin/usage',          label: 'Usage',          icon: 'bi-bar-chart-line' },
  { path: '/app-admin/logs',           label: 'Logs',           icon: 'bi-journal-text' },
  { path: '/app-admin/security',       label: 'Security',       icon: 'bi-shield-lock' },
  { path: '/app-admin/maintenance',    label: 'Maintenance',    icon: 'bi-tools' },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { branding } = useOrg();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [appVersion, setAppVersion] = useState(null);

  const isAppAdmin = user?.role === 'app_admin';

  useEffect(() => {
    if (isAppAdmin) return;
    const fetch = () => client.get('/dashboard/status').then(r => setStatus(r.data)).catch(() => {});
    fetch();
    const t = setInterval(fetch, 30000);
    return () => clearInterval(t);
  }, [isAppAdmin]);

  useEffect(() => {
    client.get('/security/version', { params: { _t: Date.now() } })
      .then(r => setAppVersion(r.data.version))
      .catch(() => {});
  }, []);

  const role = user?.role || 'reviewer';
  const visibleNav = isAppAdmin
    ? appAdminItems
    : navItems.filter(item => item.roles.includes(role));

  const healthColor = status?.running ? '#ffc107' : status?.error ? '#dc3545' : '#28a745';
  const healthLabel = status?.running
    ? `Running: ${status.stage || '...'}`
    : status?.error ? 'Error' : 'System OK';

  const primaryColor = branding.primary_color || '#B71530';

  return (
    <nav className="col-md-3 col-lg-2 d-none d-md-block bg-light sidebar">
      <div className="sidebar-sticky">
        {/* Navigation */}
        <span className="sidebar-heading">
          {isAppAdmin ? 'App Admin' : 'Thx Studio'}
        </span>
        <ul className="nav flex-column mb-2">
          {visibleNav.map(item => (
            <li className="nav-item" key={item.path}>
              <NavLink
                to={item.path}
                end
                className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                style={({ isActive }) => isActive ? { color: primaryColor } : {}}
              >
                <i className={`bi ${item.icon} mr-2`} style={{ fontSize: '14px' }} />
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* System status (org users only) */}
        {!isAppAdmin && (
          <>
            <span className="sidebar-heading mt-3">System</span>
            <ul className="nav flex-column mb-2">
              <li className="nav-item">
                <span className="nav-link" style={{ cursor: 'default', fontSize: '12px', paddingTop: '5px', paddingBottom: '5px' }}>
                  <span style={{ color: healthColor, marginRight: '5px' }}>●</span>
                  {healthLabel}
                </span>
              </li>
              {status?.last_run && (
                <li className="nav-item">
                  <span className="nav-link text-muted" style={{ cursor: 'default', fontSize: '11px', paddingTop: '2px', paddingBottom: '5px' }}>
                    Last run: {new Date(status.last_run).toLocaleString('en-GB', {
                      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
                    })}
                  </span>
                </li>
              )}
              <li className="nav-item">
                <button
                  className="nav-link"
                  onClick={async () => { await logout(); navigate('/login'); }}
                  style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '12px', color: '#dc3545', paddingTop: '5px', paddingBottom: '5px' }}
                >
                  <i className="bi bi-box-arrow-right mr-2" />
                  Sign Out
                </button>
              </li>
            </ul>
          </>
        )}

        {/* App admin sign out */}
        {isAppAdmin && (
          <ul className="nav flex-column mb-2 mt-3">
            <li className="nav-item">
              <button
                className="nav-link"
                onClick={async () => { await logout(); navigate('/login'); }}
                style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '12px', color: '#dc3545' }}
              >
                <i className="bi bi-box-arrow-right mr-2" />
                Sign Out
              </button>
            </li>
          </ul>
        )}

        {/* App version */}
        {appVersion && (
          <div style={{ padding: '6px 16px 10px', borderTop: '1px solid #e9ecef', marginTop: '4px' }}>
            <span style={{ fontSize: '11px', color: '#adb5bd', fontFamily: 'monospace' }}>
              v{appVersion}
            </span>
          </div>
        )}
      </div>
    </nav>
  );
}
